import { useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { motion } from "motion/react";
import { ChevronRight, Layers, Lock, Search } from "lucide-react";
import { seo, breadcrumbScript } from "@/lib/seo";
import { PrereqSheet } from "@/components/prereq/prereq-sheet";
import { EXAM_LABEL, SUBJECT_LABEL } from "@/lib/pyq/analytics";
import { getPrereqChapters, prereqSubjects, searchPrereqChapters } from "@/lib/prereq/selectors";
import type { ChapterPrerequisite, ExamId, SubjectId } from "@/lib/prereq/types";
import { FREE_CHAPTER_LIMIT, usePaidAccess } from "@/components/billing/feature-lock";
import { useUpgrade } from "@/components/billing/premium-lock";
import { cn } from "@/lib/utils";
import { ShareButton } from "@/components/app/share-button";

export const Route = createFileRoute("/prerequisites")({
  head: () => ({
    ...seo({
      title: "Chapter Prerequisites — Before You Start | JEE & NEET",
      description:
        "See the mandatory chapters, core skills and common mistakes to review before starting any JEE or NEET chapter.",
      path: "/prerequisites",
    }),
    scripts: [
      breadcrumbScript([
        { name: "Home", path: "/" },
        { name: "Chapter Prerequisites", path: "/prerequisites" },
      ]),
    ],
  }),
  component: PrerequisitesPage,
});

const CARD =
  "rounded-3xl border border-white/10 bg-card/70 shadow-[0_8px_30px_rgb(0,0,0,0.12)] backdrop-blur-xl";
const PILL =
  "rounded-full border px-3 py-1.5 text-[12px] font-semibold transition-all active:scale-95";

function Segmented<T extends string>({
  options,
  value,
  onChange,
}: {
  options: { id: T; label: string }[];
  value: T;
  onChange: (id: T) => void;
}) {
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((o) => (
        <button
          key={o.id}
          type="button"
          onClick={() => onChange(o.id)}
          className={cn(
            PILL,
            value === o.id
              ? "border-primary/40 bg-primary/15 text-primary"
              : "border-white/10 bg-card/60 text-muted-foreground",
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function PrerequisitesPage() {
  const [exam, setExam] = useState<ExamId>("jee");
  const subjects = prereqSubjects(exam);
  const [subject, setSubject] = useState<SubjectId | null>(null);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState<ChapterPrerequisite | null>(null);
  const { paid } = usePaidAccess();
  const { openUpgrade } = useUpgrade();

  const currentSubject = subject && subjects.includes(subject) ? subject : (subjects[0] ?? null);
  const chapters = useMemo(
    () => (currentSubject ? getPrereqChapters(exam, currentSubject) : []),
    [exam, currentSubject],
  );
  const visible = useMemo(() => searchPrereqChapters(chapters, query), [chapters, query]);
  // Free access: the first three chapters of each subject, in dataset order.
  const freeIds = useMemo(
    () => new Set(chapters.slice(0, FREE_CHAPTER_LIMIT).map((c) => c.id)),
    [chapters],
  );
  const isLocked = (c: ChapterPrerequisite) => !paid && !freeIds.has(c.id);
  const openChapter = (c: ChapterPrerequisite) => {
    if (isLocked(c)) {
      setActive(null);
      openUpgrade("Chapter Prerequisites premium chapters");
      return;
    }
    setActive(c);
  };

  return (
    <div className="mx-auto w-full max-w-3xl px-4 pb-28 pt-6">
      <header className="flex items-center gap-3">
        <span className="grid size-11 place-items-center rounded-2xl bg-gradient-to-br from-sky-400/25 to-indigo-500/10 text-sky-500 dark:text-sky-300">
          <Layers className="size-5" />
        </span>
        <div>
          <h1 className="text-[22px] font-black tracking-tight">Chapter Prerequisites</h1>
          <p className="text-[12.5px] text-muted-foreground">
            Make sure you are comfortable with these foundations before starting a chapter.
          </p>
        </div>
        <ShareButton className="ml-auto" />
      </header>




      {subjects.length === 0 ? (
        <div className={cn(CARD, "mt-6 p-6 text-center")}>
          <p className="text-[14px] font-bold">Prerequisite data not added yet</p>
          <p className="mt-1 text-[12.5px] text-muted-foreground">
            Chapters will appear here as soon as the prerequisite dataset is added.
          </p>
        </div>
      ) : (
        <>
          <div className="mt-5 space-y-3">
            <Segmented
              options={(["jee", "neet"] as ExamId[]).map((id) => ({ id, label: EXAM_LABEL[id] }))}
              value={exam}
              onChange={(id) => {
                setExam(id);
                setSubject(null);
              }}
            />
            {currentSubject ? (
              <Segmented
                options={subjects.map((id) => ({ id, label: SUBJECT_LABEL[id] }))}
                value={currentSubject}
                onChange={setSubject}
              />
            ) : null}

            <label className={cn(CARD, "flex items-center gap-2 px-4 py-3")}>
              <Search className="size-4 text-muted-foreground" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search chapters"
                className="w-full bg-transparent text-[13.5px] outline-none placeholder:text-muted-foreground"
              />
            </label>
          </div>

          <div className="mt-4 space-y-2.5">
            {visible.map((chapter, i) => (
              <motion.button
                key={chapter.id}
                type="button"
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: Math.min(i * 0.02, 0.2) }}
                onClick={() => openChapter(chapter)}
                className={cn(CARD, "flex w-full items-center gap-3 p-4 text-left active:scale-[0.99]")}
              >
                <div className="min-w-0 flex-1">
                  <p className="flex items-center gap-2 text-[14px] font-bold">
                    <span className="truncate">{chapter.chapter}</span>
                    {isLocked(chapter) ? (
                      <Lock className="size-3.5 shrink-0 text-muted-foreground" />
                    ) : null}
                  </p>
                  <p className="mt-1 flex flex-wrap items-center gap-1.5 text-[11px] text-muted-foreground">
                    <span className="rounded-full border border-white/10 bg-surface px-2 py-0.5 font-semibold">
                      Class {chapter.classLevel}
                    </span>
                    {chapter.difficultyRating ? (
                      <span className="rounded-full border border-amber-400/25 bg-amber-400/10 px-2 py-0.5 font-bold text-amber-600 dark:text-amber-300">
                        {chapter.difficultyRating}
                      </span>
                    ) : null}
                    <span>{isLocked(chapter) ? "Premium — tap to unlock" : "Tap to view"}</span>
                  </p>
                </div>
                <ChevronRight className="size-4 shrink-0 text-muted-foreground" />
              </motion.button>
            ))}
            {visible.length === 0 ? (
              <p className="py-8 text-center text-[13px] text-muted-foreground">
                No chapters match your search.
              </p>
            ) : null}
          </div>
        </>
      )}

      <PrereqSheet
        chapter={active}
        chapters={chapters}
        onClose={() => setActive(null)}
        onOpenChapter={openChapter}
      />
    </div>
  );
}
